import { useState } from "react";
import { BUSINESS, CONTACT_TOPICS } from "#/lib/constants";
import { sendContactEmailAction } from "#/lib/send-email";
import { Loader2, Check, ArrowRight, ChevronDown } from "lucide-react";

/**
 * ContactForm — the general-inquiry form for /contact: a single light,
 * rectangular panel with plain stacked fields (label above input, no
 * dividing hairlines, no bordered cell grid). This is the deliberate
 * differentiator from `EstimateForm` on /quote, which reads as a bordered
 * work-order spec sheet. Light on purpose: customer-facing forms are never
 * dark on this site (see memory/lessons.md 2026-07-25). No service/property/
 * address fields, those belong to the quote form. Owns its own state, no props.
 */
export function ContactForm() {
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [honeypot, setHoneypot] = useState("");

  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);
    const form = e.currentTarget;
    const val = (name: string) =>
      (form.elements.namedItem(name) as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement)
        .value;
    const data = {
      name: val("name"),
      email: val("email"),
      phone: val("phone") || undefined,
      inquiryType: val("inquiryType") || undefined,
      message: val("message"),
      _hp: honeypot,
    };
    try {
      const result = await sendContactEmailAction({ data });
      if (result.success) setSubmitted(true);
      else setError(result.error || "Something went wrong. Please try again.");
    } catch {
      setError(`An error occurred. Please call us at ${BUSINESS.phone}.`);
    } finally {
      setIsSubmitting(false);
    }
  }

  if (submitted) return <SuccessPanel phone={BUSINESS.phone} />;

  return (
    <form
      onSubmit={onSubmit}
      className="rounded-none border border-graphite/10 bg-white px-6 py-8 shadow-sm sm:px-10 sm:py-10"
    >
      {/* Honeypot — hidden from users, catches bots */}
      <input
        type="text"
        name="_hp"
        value={honeypot}
        onChange={(e) => setHoneypot(e.target.value)}
        style={{ position: "absolute", left: "-9999px", opacity: 0, height: 0, width: 0 }}
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
      />

      <p className="text-xs font-bold uppercase tracking-[0.2em] text-flame">Send a Message</p>
      <h2 className="font-heading mt-3 text-2xl font-black uppercase leading-[0.95] tracking-tight text-graphite sm:text-3xl">
        We&apos;d like to hear from you
      </h2>
      <p className="mt-3 max-w-lg text-sm leading-relaxed text-concrete-dark">
        Questions, callback requests, or anything not covered by a project quote. Fill this out
        and we&apos;ll get back to you, usually the same business day.
      </p>

      <div className="mt-8 space-y-6">
        <Field label="Name" htmlFor="name" required>
          <input
            id="name"
            name="name"
            type="text"
            required
            autoComplete="name"
            placeholder="Jane Doe"
            className={inputCls}
          />
        </Field>

        <div className="grid gap-6 sm:grid-cols-2">
          <Field label="Email" htmlFor="email" required>
            <input
              id="email"
              name="email"
              type="email"
              required
              inputMode="email"
              autoComplete="email"
              placeholder="jane@example.com"
              className={inputCls}
            />
          </Field>
          <Field label="Phone" htmlFor="phone" optional>
            <input
              id="phone"
              name="phone"
              type="tel"
              inputMode="tel"
              autoComplete="tel"
              placeholder="(302) 555-0123"
              className={inputCls}
            />
          </Field>
        </div>

        <Field label="Reason for contacting" htmlFor="inquiryType" optional>
          <SelectField id="inquiryType" defaultValue="">
            <option value="" disabled hidden>
              Select a reason
            </option>
            {CONTACT_TOPICS.map((topic) => (
              <option key={topic} value={topic} className="text-graphite">
                {topic}
              </option>
            ))}
          </SelectField>
        </Field>

        <Field label="Message" htmlFor="message" required>
          <textarea
            id="message"
            name="message"
            required
            rows={5}
            autoComplete="off"
            placeholder="How can we help?"
            className={`${inputCls} resize-none`}
          />
        </Field>
      </div>

      {error && (
        <div role="alert" className="mt-6 border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      <button
        type="submit"
        disabled={isSubmitting}
        className="group mt-8 flex min-h-11 w-full items-center justify-between gap-4 bg-flame px-6 py-4 font-heading text-base font-bold uppercase tracking-wide text-white transition-colors hover:bg-flame-dark disabled:opacity-70 sm:px-8"
      >
        <span className="flex items-center gap-3">
          {isSubmitting && <Loader2 className="h-5 w-5 animate-spin" />}
          {isSubmitting ? "Sending…" : "Send Message"}
        </span>
        <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
      </button>
    </form>
  );
}

const inputCls =
  "w-full min-h-11 border-b border-gray-300 bg-transparent py-2.5 text-base text-graphite outline-none transition-colors placeholder:text-concrete focus:border-flame";

function Field({
  label,
  htmlFor,
  required,
  optional,
  children,
}: {
  label: string;
  htmlFor: string;
  required?: boolean;
  optional?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label htmlFor={htmlFor} className="mb-2 block text-[0.7rem] font-bold uppercase tracking-[0.15em] text-graphite">
        {label}
        {required && <span className="text-flame"> *</span>}
        {optional && (
          <span className="font-medium normal-case tracking-normal text-concrete"> (optional)</span>
        )}
      </label>
      {children}
    </div>
  );
}

function SelectField({
  id,
  defaultValue,
  children,
}: {
  id: string;
  defaultValue: string;
  children: React.ReactNode;
}) {
  return (
    <div className="relative">
      <select
        id={id}
        name={id}
        defaultValue={defaultValue}
        className={`${inputCls} appearance-none pr-8 text-concrete valid:text-graphite`}
      >
        {children}
      </select>
      <ChevronDown className="pointer-events-none absolute right-0 top-1/2 h-5 w-5 -translate-y-1/2 text-concrete" />
    </div>
  );
}

function SuccessPanel({ phone }: { phone: string }) {
  return (
    <div className="rounded-none border border-flame/30 bg-white px-6 py-12 text-center shadow-sm sm:px-12">
      <div className="mx-auto flex h-14 w-14 items-center justify-center bg-flame text-white">
        <Check className="h-8 w-8" strokeWidth={3} />
      </div>
      <h2 className="font-heading mt-5 text-2xl font-black uppercase tracking-tight text-graphite sm:text-3xl">
        Message sent
      </h2>
      <p className="mx-auto mt-3 max-w-md text-concrete-dark">
        Thanks for reaching out. We&apos;ll get back to you soon, usually the same business day.
        Need us sooner? Call {phone}.
      </p>
    </div>
  );
}
